import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

const BREAKPOINTS = {
  SMALL: 768,
  MEDIUM: 1024,
  LARGE: 1280,
  MAXIMUM: 1344,
};
/**
 * Format for configurable text
 * @example
 *   "title": {
 *      "value": "Text Content",
 *      "tag": "h4",
 *      "align": "center",
 *      "text_transform": "uppercase",
 *      "letter_spacing": "1.8px",
 *      "color": "var(--text--1)",
 *      "font_family": "Lato, sans-serif",
 *      "font_style": "normal",
 *      "font_weight": "900"
 *   }
 */

export interface UITextConfig {
  value: string;
  tag:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 't-title'
    | 't-data'
    | 't-caption';
  align: 'left' | 'center' | 'right';
  text_transform: 'capitalize' | 'uppercase' | 'lowercase' | 'none';
  letter_spacing: string;
  color: string;
  font_family: string;
  font_style: string;
  font_weight:
    | 'var(--thin)'
    | '100'
    | 'var(--extra-light)'
    | '200'
    | 'var(--light)'
    | '300'
    | 'var(--regular)'
    | '400'
    | 'var(--medium)'
    | '500'
    | 'var(--semi-bold)'
    | '600'
    | 'var(--bold)'
    | '700'
    | 'var(--extra-bold)'
    | '800'
    | 'var(--heavy)'
    | '900';
}

/**
 * Sets CSS text properties for a given HTML element based on configuration
 * @example
 *   <div [ui-text]="config.title"></div>
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[ui-text]',
})
export class UITextDirective implements AfterViewInit, OnChanges {
  @Input('ui-text') config!: UITextConfig;

  @HostListener('window:resize') onResize() {
    this.renderText();
  }

  get isObj(): boolean {
    return typeof this.config === 'object';
  }

  /**
   * returns the appropriate variable given the current screen size & configuration
   * see: design-system/assets/styles/base/_type.scss for reference
   */
  get size(): string {
    if (window.innerWidth < BREAKPOINTS.SMALL && this.isObj) {
      const mobileSizes: Record<string, string> = {};

      mobileSizes['h1'] =
        mobileSizes['h2'] =
        mobileSizes['h3'] =
          't-mobile-header';
      mobileSizes['h4'] = mobileSizes['h5'] = 't-mobile-subheader';
      mobileSizes['h6'] = 't-title';

      const mobileSize = mobileSizes[this.config.tag];

      return mobileSize ? mobileSize : this.config.tag;
    } else {
      return this.config.tag;
    }
  }

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit() {
    this.renderText();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      this.renderText();
    }
  }

  renderText() {
    if (this.config && this.isObj) {
      const el = this.elementRef.nativeElement;

      this.renderer.setStyle(
        el,
        'color',
        this.config.color || 'var(--text--1)',
      );
      this.renderer.setStyle(el, 'text-align', this.config.align || 'left');
      this.renderer.setStyle(
        el,
        'text-transform',
        this.config.text_transform || 'none',
      );

      this.renderer.setStyle(el, 'font-size', `var(--${this.size}-font-size)`);
      this.renderer.setStyle(
        el,
        'line-height',
        `var(--${this.size}-line-height)`,
      );
      this.renderer.setStyle(
        el,
        'letter-spacing',
        this.config.letter_spacing || '0',
      );

      this.renderer.setStyle(
        el,
        'font-family',
        this.config.font_family || 'var(--typeface-1-name)',
      );
      this.renderer.setStyle(
        el,
        'font-weight',
        this.config.font_weight || 'var(--regular)',
      );
      this.renderer.setStyle(
        el,
        'font-style',
        this.config.font_style || 'normal',
      );
    }

    // if the text config is an object, use the `value` prop, else use the string value
    // Since null will evaluate to an object, also check for falsy config.value
    const textToDisplay = this.config?.value || this.config;

    this.elementRef.nativeElement.innerHTML = textToDisplay;
  }
}
